import { useState } from 'react';
import type { ConnectionChannel, ConnectionFamily } from '@qm/contracts/connection-groups';
import { Button, Heading, Text } from '@qm/ui';
import { channelName, groupCopy } from './group-copy';
import { BrandLogo } from '../../../shell/BrandLogo';
import { InAppBrowserNotice } from '../../../shell/InAppBrowserNotice';
import { connectGate, currentInAppBrowser, type InAppBrowser } from '../../../lib/in-app-browser';
import './connection-groups.css';
export interface ConnectionGroupsProps {
  locale: 'en' | 'ar';
  busy: boolean;
  connections: { provider: string; status: string }[] | null;
  social: { provider: string; status: string }[] | null;
  onConnect: (family: ConnectionFamily, channels: ConnectionChannel[]) => void;
  /** in-app browser override (tests); defaults to reading the current browser */
  inAppBrowser?: InAppBrowser | null;
}
export function ConnectionGroups({
  locale,
  busy,
  connections,
  social,
  onConnect: connectNow,
  inAppBrowser,
}: ConnectionGroupsProps) {
  const t = groupCopy(locale);
  // Meta blocks its login in in-app browsers (Outlook, Facebook, ...): suggest Safari/Chrome first, once
  const [gate] = useState(() => connectGate(inAppBrowser === undefined ? currentInAppBrowser() : inAppBrowser));
  const [held, setHeld] = useState<{ family: ConnectionFamily; channels: ConnectionChannel[] } | null>(null);
  const onConnect = (family: ConnectionFamily, channels: ConnectionChannel[]) => {
    if (gate.shouldHold()) { setHeld({ family, channels }); return; }
    connectNow(family, channels);
  };
  const continueAnyway = () => {
    gate.acknowledge();
    const h = held;
    setHeld(null);
    if (h) connectNow(h.family, h.channels);
  };
  const [separate, setSeparate] = useState({ meta: false, google: false });
  const [chosen, setChosen] = useState<Record<ConnectionFamily, ConnectionChannel[]>>({
    meta: ['facebook', 'instagram'],
    google: ['gbp', 'google_search_console'],
  });
  const toggle = (family: ConnectionFamily, channel: ConnectionChannel, on: boolean) =>
    setChosen((s) => ({
      ...s,
      [family]: on ? [...s[family], channel] : s[family].filter((c) => c !== channel),
    }));
  const status = (channel: ConnectionChannel) => {
    const rows = channel === 'facebook' || channel === 'instagram' ? social : connections;
    if (rows === null) return 'unknown';
    if (rows.some((r) => r.provider === channel && r.status === 'connected')) return 'connected';
    return rows.some(
      (r) => r.provider === channel && !['revoked', 'disconnected'].includes(r.status),
    )
      ? 'attention'
      : 'not_connected';
  };
  return (
    <div className="qm-connection-groups">
      {held ? (
        <InAppBrowserNotice
          copy={{ title: t.inAppTitle, body: t.inAppBody, copyLink: t.inAppCopy, copied: t.inAppCopied, copyManual: t.inAppManual, continueAnyway: t.inAppContinue }}
          onContinue={continueAnyway}
        />
      ) : null}
      {(['meta', 'google'] as const).map((family) => {
        const title = family === 'meta' ? 'Meta' : 'Google';
        const channels: ConnectionChannel[] =
          family === 'meta' ? ['facebook', 'instagram'] : ['gbp', 'google_search_console'];
        const ads = family === 'meta' ? 'meta' : 'google_ads';
        return (
          <article
            key={family}
            data-family={family}
            className="qm-connection-family"
            aria-labelledby={`connect-${family}`}
          >
            <header>
              <BrandLogo provider={family} size={52} />
              <div>
                <Heading level={3} id={`connect-${family}`}>
                  {title}
                </Heading>
                <Text tone="muted">{family === 'meta' ? t.metaHint : t.googleHint}</Text>
              </div>
            </header>
            <fieldset disabled={busy}>
              <legend className="qm-sr-only">
                {t.chooseServices}: {title}
              </legend>
              {channels.map((channel) => {
                const state = status(channel);
                return (
                  <div className="qm-connection-service" data-channel={channel} key={channel}>
                    <label>
                      <input
                        type="checkbox"
                        checked={chosen[family].includes(channel)}
                        onChange={(e) => toggle(family, channel, e.target.checked)}
                      />
                      <BrandLogo provider={channel} decorative />
                      <span>{channelName(channel, locale)}</span>
                    </label>
                    <span data-status={state}>
                      {state === 'connected'
                        ? t.connected
                        : state === 'attention'
                          ? t.pending
                          : state === 'unknown'
                            ? t.statusUnknown
                            : t.unconnected}
                    </span>
                  </div>
                );
              })}
              <label className="qm-connection-option">
                <input
                  type="checkbox"
                  checked={chosen[family].includes(ads)}
                  onChange={(e) => toggle(family, ads, e.target.checked)}
                />
                <BrandLogo provider={ads} decorative />
                {t.ads}
              </label>
              <label className="qm-connection-option">
                <input
                  type="checkbox"
                  checked={separate[family]}
                  onChange={(e) => setSeparate((s) => ({ ...s, [family]: e.target.checked }))}
                />
                {t.separate}
              </label>
              <Text tone="muted">{separate[family] ? t.separateHint : t.sharedHint}</Text>
              <div className="qm-connection-buttons">
                {separate[family] ? (
                  chosen[family].map((channel) => (
                    <Button
                      type="button"
                      key={channel}
                      disabled={busy}
                      onClick={() => onConnect(family, [channel])}
                    >
                      {t.connect} {channelName(channel, locale)}
                    </Button>
                  ))
                ) : (
                  <Button
                    type="button"
                    disabled={busy || !chosen[family].length}
                    onClick={() => onConnect(family, chosen[family])}
                  >
                    {t.connect} {title}
                  </Button>
                )}
              </div>
            </fieldset>
          </article>
        );
      })}
      <Text tone="muted">{t.noAutomatic}</Text>
    </div>
  );
}
